import { z } from "zod";

import { API } from "@js/api";
import type { RootState } from "@js/store";
import type { Address } from "@js/types/common";
import type { PaginatedResult } from "@js/types/generic";
import type {
  EmployerInvoiceMinimal,
  EmployerInvoicesStatusCount,
  FreelancerInvoice,
} from "@js/types/invoices";
import { getOrderingParam } from "@js/utils";

import type {
  CalculateEmployerInvoicesCreditCardFeeParams,
  CreateFreelancerInvoiceValues,
  EmployerInvoicesCreditCardFee,
  GetAllEmployerInvoicesIdsParams,
  GetEmployerConsolidatedInvoicesParams,
  GetEmployerConsolidatedInvoicesResponse,
  GetEmployerInvoicesMinimalParams,
  GetEmployerInvoicesParams,
  GetEmployerInvoicesResponse,
  GetEmployerInvoiceStatisticsResponse,
} from "./types";
import {
  processEmployerInvoicesCommonParams,
  processEmployerInvoicesParams,
} from "./utils";

const EmployerMinimalSchema = z.object({
  name: z.string(),
  id: z.number(),
});

export const FreelancerJobToIssueInvoiceSchema = z.object({
  employer: EmployerMinimalSchema,
  id: z.number(),
  invoice_can_be_created: z.boolean(),
  invoice_cannot_be_created_reason: z.string(),
  title: z.string(),
});

export type FreelancerJobToIssueInvoice = z.infer<
  typeof FreelancerJobToIssueInvoiceSchema
>;

export type InitialInvoiceData = {
  company_node: {
    name: string;
    slug: string;
  };
  new_invoice_number: string;
  po_number_required_since: string | null;
  previous_po_numbers: string[];
  wise_fee: string | null;
  offer_requiring_acceptance_id: number | null;
  talent_invoice_editing_disabled: boolean;
  offer_pay_rate: string | null;
};

type InitialInvoiceDataRequestPayload = { job: number };

export const invoiceApi = API.injectEndpoints({
  endpoints: (build) => ({
    getFreelancerInvoice: build.query<FreelancerInvoice, number>({
      query: (id) => ({
        url: `/freelancer_invoices/${id}/`,
        method: "GET",
      }),
    }),

    getFreelancerInvoices: build.query<
      PaginatedResult<FreelancerInvoice>,
      { page: number }
    >({
      query: ({ page }) => ({
        url: `/freelancer_invoices/?page_size=5&page=${page}`,
        method: "GET",
      }),
    }),

    markFeelancerInvoicesAsPaid: build.mutation<void, { invoices: number[] }>({
      query: (data) => {
        return {
          url: `/freelancer_invoices/mark_as_paid/`,
          method: "POST",
          data,
        };
      },
    }),

    getFreelancerJobsToIssueInvoice: build.query<
      FreelancerJobToIssueInvoice[],
      void
    >({
      query: () => ({
        url: "/freelancer_jobs_select_project/",
        method: "GET",
      }),
    }),
    getInitialInvoiceData: build.query<
      InitialInvoiceData,
      InitialInvoiceDataRequestPayload
    >({
      query: ({ job }) => ({
        url: `/freelancer_invoices/initial_invoice_data/`,
        params: { job },
        method: "GET",
      }),
      keepUnusedDataFor: 5,
    }),
    getEmployerAddress: build.query<Address, number | "all">({
      query: (id) => ({
        url: `/freelancer_employers/${id === "all" ? "" : `${id}/`}`,
        method: "GET",
      }),
      transformResponse(response: { address: Address }) {
        return response.address;
      },
    }),
  }),
});

export const {
  useGetFreelancerInvoiceQuery,
  useGetFreelancerInvoicesQuery,
  useGetFreelancerJobsToIssueInvoiceQuery,
  useLazyGetInitialInvoiceDataQuery,
  useGetInitialInvoiceDataQuery,
  useGetEmployerAddressQuery,
  useLazyGetEmployerAddressQuery,
  useMarkFeelancerInvoicesAsPaidMutation,
} = invoiceApi;

export const employerInvoicesApi = API.injectEndpoints({
  endpoints: (build) => ({
    getAllEmployerInvoicesIds: build.query<
      number[],
      GetAllEmployerInvoicesIdsParams | void
    >({
      query: (params) => {
        const processedParams = processEmployerInvoicesCommonParams(
          params ?? {},
        );

        return {
          url: `/employer_invoices/all_invoices_ids/`,
          method: "GET",
          params: processedParams,
        };
      },
      providesTags: ["EmployerInvoicesIds"],
    }),

    getEmployerInvoicesMinimal: build.query<
      EmployerInvoiceMinimal[],
      GetEmployerInvoicesMinimalParams
    >({
      query: (params) => ({
        url: `/employer_invoices_minimal/`,
        method: "GET",
        params: {
          ids: params.ids.join(","),
        },
      }),
      providesTags: ["EmployerInvoicesMinimal"],
    }),

    getEmployerInvoiceStatistics: build.query<
      GetEmployerInvoiceStatisticsResponse,
      void
    >({
      query: () => ({
        url: `/invoice_statistics/employer/`,
        method: "GET",
      }),
      providesTags: ["EmployerInvoiceStatistics"],
    }),

    getEmployerInvoices: build.query<
      GetEmployerInvoicesResponse,
      GetEmployerInvoicesParams
    >({
      query: (params) => {
        const processedParams = processEmployerInvoicesParams(params);

        return {
          url: `/employer_invoices/`,
          method: "GET",
          params: processedParams,
        };
      },
      providesTags: [{ type: "EmployerInvoices", id: "LIST" }],
    }),

    markEmployerInvoiceAsViewed: build.mutation<void, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/employer_invoices/${id}/mark_as_viewed/`,
          method: "POST",
        };
      },
      async onQueryStarted({ id }, { queryFulfilled, dispatch, getState }) {
        const state = getState() as RootState;
        const patches: Array<{ undo: () => void }> = [];
        const allGetArgs = employerInvoicesApi.util.selectCachedArgsForQuery(
          state,
          "getEmployerInvoices",
        );

        allGetArgs.forEach((arg) => {
          const patchResult = dispatch(
            employerInvoicesApi.util.updateQueryData(
              "getEmployerInvoices",
              arg,
              (draft) => {
                const invoice = draft?.results?.find(
                  (employerInvoice) => employerInvoice.id === id,
                );
                if (!invoice) {
                  return;
                }
                invoice.first_viewed_at = new Date().toISOString();
              },
            ),
          );

          patches.push(patchResult);
        });
        try {
          await queryFulfilled;
        } catch (error) {
          void error;
        }
      },
    }),

    markEmployerInvoicesAsPaid: build.mutation<void, { invoices: number[] }>({
      query: (data) => {
        return {
          url: `/employer_invoices/mark_as_paid/`,
          method: "POST",
          data,
        };
      },
    }),

    calculateEmployerInvoicesCreditCardFee: build.mutation<
      EmployerInvoicesCreditCardFee,
      CalculateEmployerInvoicesCreditCardFeeParams
    >({
      query: (data) => ({
        url: `/employer_invoices/calculate_credit_card_fee/`,
        method: "POST",
        data,
      }),
    }),

    createFreelancerInvoice: build.mutation<
      void,
      CreateFreelancerInvoiceValues
    >({
      query: (data) => ({
        url: `/freelancer_invoices/`,
        method: "POST",
        data,
      }),
    }),

    getEmployerInvoicesStatusCounts: build.query<
      EmployerInvoicesStatusCount[],
      void
    >({
      query: () => ({
        url: `/employer_invoices/status_counts/`,
        method: "GET",
      }),
      providesTags: ["EmployerInvoiceStatusCounts"],
    }),

    getEmployerConsolidatedInvoices: build.query<
      GetEmployerConsolidatedInvoicesResponse,
      GetEmployerConsolidatedInvoicesParams
    >({
      query: (params) => {
        const { order_by, order_dir, ...rest } = params;
        const ordering = getOrderingParam({ order_by, order_dir });

        return {
          url: `/employer_consolidated_invoices/`,
          method: "GET",
          params: { ordering, ...rest },
        };
      },
      providesTags: [{ type: "EmployerConsolidatedInvoices", id: "LIST" }],
    }),
  }),
});

export const {
  useLazyGetAllEmployerInvoicesIdsQuery,
  useGetEmployerInvoicesMinimalQuery,
  useLazyGetEmployerInvoicesMinimalQuery,
  useGetEmployerInvoiceStatisticsQuery,
  useGetEmployerInvoicesQuery,
  useGetEmployerInvoicesStatusCountsQuery,
  useGetEmployerConsolidatedInvoicesQuery,
  useCalculateEmployerInvoicesCreditCardFeeMutation,
  useMarkEmployerInvoiceAsViewedMutation,
  useMarkEmployerInvoicesAsPaidMutation,
  useCreateFreelancerInvoiceMutation,
} = employerInvoicesApi;
